<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatDistanceToNow, parseISO } from 'date-fns';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import ChannelIcon from 'dashboard/components-next/icon/ChannelIcon.vue';

const props = defineProps({
  task: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['openConversation', 'edit', 'delete']);

const { t } = useI18n();

const PRIORITY_STYLES = {
  urgent: 'text-r-400',
  high: 'text-o-400',
  medium: 'text-y-500',
  low: 'text-n-slate-10',
};

const PRIORITY_ICONS = {
  urgent: 'i-lucide-chevrons-up',
  high: 'i-lucide-chevron-up',
  medium: 'i-lucide-equal',
  low: 'i-lucide-chevron-down',
};

const conversation = computed(() => props.task.conversations?.[0]);
const contactName = computed(() => conversation.value?.contact?.name);

const priorityClass = computed(() => PRIORITY_STYLES[props.task.priority]);
const priorityIcon = computed(() => PRIORITY_ICONS[props.task.priority]);

const isDueToday = computed(() => {
  if (!props.task.due_date || props.task.overdue) return false;
  const due = new Date(props.task.due_date);
  const now = new Date();
  return due.toDateString() === now.toDateString();
});

// step_changed_at vem como string ISO da API do Kanban (não Unix). Aceita ambos
// e protege contra Invalid Date — senão o render do card lança RangeError.
const stepTime = computed(() => {
  const value = props.task.step_changed_at;
  if (!value) return null;
  const date =
    typeof value === 'number' ? new Date(value * 1000) : parseISO(value);
  return Number.isNaN(date.getTime())
    ? null
    : formatDistanceToNow(date, { addSuffix: true });
});

const onClick = () => {
  if (conversation.value) {
    emit('openConversation', conversation.value);
  } else {
    emit('edit', props.task);
  }
};
</script>

<template>
  <div
    class="relative flex flex-col gap-1 px-3 py-2.5 rounded-lg cursor-pointer group bg-n-solid-1 hover:bg-n-solid-3"
    :class="{ 'ring-1 ring-r-400': task.overdue }"
    @click="onClick"
  >
    <!-- Ações no hover -->
    <div
      class="absolute flex items-center gap-1 transition-opacity opacity-0 top-2 ltr:right-2 rtl:left-2 group-hover:opacity-100"
    >
      <span
        class="p-1 rounded bg-n-alpha-2 hover:bg-n-alpha-3"
        role="button"
        :title="t('KANBAN.TASK.DELETE')"
        @click.stop="emit('delete', task)"
      >
        <Icon icon="i-lucide-trash-2" class="size-3.5 text-n-slate-11" />
      </span>
      <span
        class="p-1 rounded bg-n-alpha-2 hover:bg-n-alpha-3"
        role="button"
        :title="t('KANBAN.TASK.EDIT_TITLE')"
        @click.stop="emit('edit', task)"
      >
        <Icon icon="i-lucide-pencil" class="size-3.5 text-n-slate-11" />
      </span>
    </div>

    <span class="text-sm font-medium text-n-slate-12 line-clamp-2 pr-2">
      {{ task.title }}
    </span>

    <p v-if="task.description" class="text-xs text-n-slate-11 line-clamp-2">
      {{ task.description }}
    </p>

    <div class="flex items-center justify-between gap-2 mt-1">
      <div class="flex items-center gap-2 min-w-0">
        <Icon
          v-if="task.priority"
          :icon="priorityIcon"
          class="flex-shrink-0 size-3.5"
          :class="priorityClass"
        />
        <span
          v-if="conversation"
          class="flex items-center gap-1 text-xs truncate text-n-slate-10"
        >
          <ChannelIcon
            :inbox="{ channel_type: conversation.channel_type }"
            use-brand-icon
            class="size-3.5"
          />
          {{ contactName || `#${conversation.display_id}` }}
        </span>
        <span
          v-if="Number(task.value)"
          class="text-xs text-n-slate-10 whitespace-nowrap"
        >
          {{ Number(task.value).toLocaleString() }}
        </span>
      </div>
      <span
        class="flex items-center flex-shrink-0 gap-1 text-xs text-n-slate-10"
      >
        <span
          v-if="task.overdue"
          class="px-1.5 py-0.5 text-white rounded bg-r-500"
        >
          {{ t('KANBAN.TASK.OVERDUE') }}
        </span>
        <span
          v-else-if="isDueToday"
          class="px-1.5 py-0.5 rounded text-n-slate-12 bg-y-300"
        >
          {{ t('KANBAN.TASK.DUE_TODAY') }}
        </span>
        <template v-if="stepTime">
          <Icon icon="i-lucide-clock" class="size-3" />
          {{ stepTime }}
        </template>
      </span>
    </div>
  </div>
</template>
